import filter from "lodash/filter";
import isEmpty from "lodash/isEmpty";
import memoize from "lodash/memoize";
import values from "lodash/values";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { isProjects, isWorkforceProfiles } from "PFApp/booking/utils/calendar_root.utils";
import { minutesToHours } from "PFCore/helpers/date";
import { ProjectItem } from "PFCore/hooks/queries/bookings/projects/use_projects_entries";
import { WorkforceItem } from "PFCore/hooks/queries/bookings/workforce/use_workforce_entries";
import { Color, Template } from "PFTypes";

import { Row } from "../../types";
import { ProfileBasic, RowOriginal, RowResource, RowType } from "../../types/row";

const getTypeFromTemplateKey = memoize(
  (templateId: number, templates: Template[]): RowType => {
    const templateKey = templates.find(({ id }) => id === templateId)?.key;
    if (templateKey?.includes("role")) {
      return RowType.Role;
    }
    if (templateKey?.includes("engagement")) {
      return RowType.Engagement;
    }
    throw new Error(`Unexpected activity template! [templateId=${templateId}]`);
  },
  (id) => id
);

const getRowType = (item: RowOriginal, templates: Template[], calendarRoot: CalendarRoot): RowType => {
  if ("bookingCategoryIds" in item) {
    return RowType.Category;
  }
  if (isProjects(calendarRoot)) {
    if ("firstName" in item || "lastName" in item) {
      return RowType.Profile;
    } else {
      return getTypeFromTemplateKey((item as ProjectItem).activity.templateId, templates);
    }
  } else {
    if ("profile" in item && !("activity" in item)) {
      return RowType.Profile;
    } else {
      // can be Role or Engagement
      return getTypeFromTemplateKey((item as ProjectItem).activity.templateId, templates);
    }
  }
};

export const getStateFromChildren = ({
  total,
  booked
}: {
  total: number;
  booked: number;
}): "booked" | "partially_booked" | "not_booked" =>
  total - booked === 0 ? "booked" : booked > 0 ? "partially_booked" : "not_booked";

const getRowTotalChildren = (item: ProjectItem, type: RowType): number => {
  if (type === RowType.Role) {
    return item.activity.vacancies?.total || 0;
  }
  if (type === RowType.Engagement) {
    return Object.values(item.activity?.childrenWorkflowStatesTally || {}).reduce(
      (curr, acc) => curr + acc,
      0
    );
  }
  return 0;
};

export const sumBookedChildren = (data: Record<string, number>) =>
  (data.booked || 0) + (data.confirmed || 0) + (data.exception || 0);

const getChildrenBooked = (item: ProjectItem, type: RowType): number => {
  if (type === RowType.Engagement) {
    return sumBookedChildren(item.activity.childrenWorkflowStatesTally ?? {});
  }

  return filter(
    values(item.activity.vacancies?.shortlists),
    ({ booked, confirmed }) => (booked ?? 0) + (confirmed ?? 0) > 0
  ).length;
};

export const getChildrenCount = ({
  original,
  type
}: Row): {
  total: number;
  booked: number;
} => {
  if (!("activity" in original)) {
    return { booked: 0, total: 0 };
  }
  return {
    booked: getChildrenBooked(original, type),
    total: getRowTotalChildren(original, type)
  };
};

const getKey = (item: RowOriginal, type: RowType, parents?: string[]): string => {
  if (!isEmpty(parents)) {
    return [...(parents || []), `${type}_${item.id}`].join("_");
  }
  return `${type}_${item.id}`;
};

export const getRowFromDataItem = (
  item: RowOriginal,
  templates: Template[],
  calendarRoot: CalendarRoot,
  parents?: string[]
): Row => {
  const type = getRowType(item, templates, calendarRoot);
  const key = getKey(item, type, parents);
  return {
    key,
    type,
    original: item,
    parents: parents || []
  };
};

export const calculateHoursBooked = (items: { duration: number }[]): number =>
  minutesToHours((items || []).reduce((acc, booking) => acc + booking.duration || 0, 0));

export const getOriginalResource = ({ type, original }: Row, calendarRoot: CalendarRoot): RowResource => {
  switch (type) {
    case RowType.Profile:
      return isWorkforceProfiles(calendarRoot)
        ? (original as WorkforceItem).profile
        : (original as ProfileBasic);
    case RowType.Role:
    case RowType.Engagement:
      return (original as ProjectItem).activity;
    case RowType.Category:
    default:
      return null;
  }
};

export const DEFAULT_OVERBOOKING_COLOR: Color = "paletteRed1";
